import { ApiError } from "../errors/ApiError.js";
import { Service } from "./Service.js";

export class ApiService extends Service {
  static Method = {
    POST: "POST",
    GET: "GET",
    PUT: "PUT",
    DELETE: "DELETE",
  };

  /**
   * @protected
   * @param {string} path - The relative path for the fetch request
   * @param {Record<string, any>} [params] - request parameters
   * @param {RequestInit} [options] - Fetch options (method, headers, body, etc.)
   * @returns {Promise<Response>} - A Promise that resolves to a Response object
   */
  async post(path, params = {}, options = {}) {
    options.method = ApiService.Method.POST;
    return await this.#fetch(path, params, options);
  }

  /**
   * @protected
   * @param {string} path - The relative path for the fetch request
   * @param {RequestInit} [options] - Fetch options (method, headers, body, etc.)
   * @returns {Promise<Response>} - A Promise that resolves to a Response object
   */
  async get(path, options = {}) {
    options.method = ApiService.Method.GET;
    return await this.#fetch(path, options);
  }

  /**
   * @protected
   * @param {string} path - The relative path for the fetch request
   * @param {Record<string, any>} [params] - request parameters
   * @param {RequestInit} [options] - Fetch options (method, headers, body, etc.)
   * @returns {Promise<Response>} - A Promise that resolves to a Response object
   */
  async put(path, params = {}, options = {}) {
    options.method = ApiService.Method.PUT;
    return await this.#fetch(path, params, options);
  }

  /**
   * @protected
   * @param {string} path - The relative path for the fetch request
   * @param {Record<string, any>} [params] - request parameters
   * @param {RequestInit} [options] - Fetch options (method, headers, body, etc.)
   * @returns {Promise<Response>} - A Promise that resolves to a Response object
   */
  async delete(path, params = {}, options = {}) {
    options.method = ApiService.Method.DELETE;
    return await this.#fetch(path, params, options);
  }

  /**
   * @protected
   * @param {Response} response
   * @param {string} message
   * @returns ApiError
   */
  buildApiError(response, message = "") {
    return new ApiError(message, response.status, response.statusText);
  }

  /**
   * @param {string} path - The relative path for the fetch request
   * @param {Record<string, any>} [params] - request parameters
   * @param {RequestInit} [options] - Fetch options (method, headers, body, etc.)
   * @returns {Promise<Response>} - A Promise that resolves to a Response object
   */
  async #fetch(path, params = {}, options = {}) {
    // Build full URL from relative path and ensure protocol is `HTTPS`.
    // For some reason, the protocol for `href` is being set to `HTTP` inside
    // of workers even though the worker and page are served over `HTTPS`.
    const url = new URL(path, location.href);
    url.protocol = "https";

    // The default value for `credentials` in a worker seems to be `omit`, but
    // we want to send cookies even in a worker, otherwise the default value is
    // `same-origin`.
    //
    // However, since the sesion cookie uses a top-level domain
    // (e.g. `.justfor.fans`) when the site domain is `dicktok.justfor.fans`,
    // the `credentials` value must be set to `include` to send the cookie.
    //
    // TODO: this breaks `staging.dicktok.com` because it is sending
    // `.justfor.fans` cookies and I don't know how that's possibly same-origin.
    options.credentials = "include";

    // Send the parameters as JSON in the body for all methods except GET
    if (options.method && options.method !== ApiService.Method.GET) {
      // Set the `Content-Type` header to `application/json`
      options.headers = {
        "Content-Type": "application/json",
        ...options.headers,
      };
      options.body = JSON.stringify(params);
    }

    return await fetch(url.href, options);
  }
}
